// src/common/components/ThemeEditor/ComponentEditors.tsx
import React from 'react';
import { Form, ColorPicker, Space, Input } from 'antd';
import type { Color } from 'antd/es/color-picker';

interface EditorProps {
  componentName: string;
  properties: Record<string, unknown>;
  onPropertyChange: (componentName: string, property: string, value: unknown) => void;
}

const ColorEdit = ({ title, property, componentName, value, onPropertyChange }: { title: string, property: string, componentName: string, value: unknown, onPropertyChange: (componentName: string, property: string, value: unknown) => void }) => (
  <Form.Item label={title}>
    <Space>
      <ColorPicker
        value={value as string || ''}
        onChange={(color: Color) => onPropertyChange(componentName, property, color.toHexString())}
        showText
      />
      <Input
        value={value as string || ''}
        onChange={(e) => onPropertyChange(componentName, property, e.target.value)}
        style={{ width: 120 }}
      />
    </Space>
  </Form.Item>
);

export const InputEditor: React.FC<EditorProps> = ({ componentName, properties, onPropertyChange }) => {
  return (
    <div>
      <ColorEdit title="Background Color" property="colorBgContainer" componentName={componentName} value={properties.colorBgContainer} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Text Color" property="colorText" componentName={componentName} value={properties.colorText} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Placeholder Text Color" property="colorTextPlaceholder" componentName={componentName} value={properties.colorTextPlaceholder} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Border Color" property="borderColor" componentName={componentName} value={properties.borderColor} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Hover Border Color" property="hoverBorderColor" componentName={componentName} value={properties.hoverBorderColor} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Active Border Color" property="activeBorderColor" componentName={componentName} value={properties.activeBorderColor} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Active Shadow" property="activeShadow" componentName={componentName} value={properties.activeShadow} onPropertyChange={onPropertyChange} />
    </div>
  );
};

export const TableEditor: React.FC<EditorProps> = ({ componentName, properties, onPropertyChange }) => {
  return (
    <div>
      <ColorEdit title="Header Background Color" property="headerBg" componentName={componentName} value={properties.headerBg} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Header Text Color" property="headerColor" componentName={componentName} value={properties.headerColor} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Row Background Color" property="colorBgContainer" componentName={componentName} value={properties.colorBgContainer} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Row Hover Background Color" property="rowHoverBg" componentName={componentName} value={properties.rowHoverBg} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Border Color" property="borderColor" componentName={componentName} value={properties.borderColor} onPropertyChange={onPropertyChange} />
    </div>
  );
};

export const CardEditor: React.FC<EditorProps> = ({ componentName, properties, onPropertyChange }) => {
  return (
    <div>
      <ColorEdit title="Background Color" property="colorBgContainer" componentName={componentName} value={properties.colorBgContainer} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Header Background Color" property="headerBg" componentName={componentName} value={properties.headerBg} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Text Color" property="colorText" componentName={componentName} value={properties.colorText} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Border Color" property="borderColor" componentName={componentName} value={properties.borderColor} onPropertyChange={onPropertyChange} />
    </div>
  );
};

export const SelectEditor: React.FC<EditorProps> = ({ componentName, properties, onPropertyChange }) => {
  return (
    <div>
      <ColorEdit title="Background Color" property="colorBgContainer" componentName={componentName} value={properties.colorBgContainer} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Text Color" property="colorText" componentName={componentName} value={properties.colorText} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Border Color" property="borderColor" componentName={componentName} value={properties.borderColor} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Option Selected Background" property="optionSelectedBg" componentName={componentName} value={properties.optionSelectedBg} onPropertyChange={onPropertyChange} />
    </div>
  );
};

export const DatePickerEditor: React.FC<EditorProps> = ({ componentName, properties, onPropertyChange }) => {
  return (
    <div>
      <ColorEdit title="Background Color" property="colorBgContainer" componentName={componentName} value={properties.colorBgContainer} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Text Color" property="colorText" componentName={componentName} value={properties.colorText} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Border Color" property="borderColor" componentName={componentName} value={properties.borderColor} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Hover Border Color" property="hoverBorderColor" componentName={componentName} value={properties.hoverBorderColor} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Active Border Color" property="activeBorderColor" componentName={componentName} value={properties.activeBorderColor} onPropertyChange={onPropertyChange} />
    </div>
  );
};

export const SwitchEditor: React.FC<EditorProps> = ({ componentName, properties, onPropertyChange }) => {
  return (
    <div>
      <ColorEdit title="Handle Background Color" property="handleBg" componentName={componentName} value={properties.handleBg} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Checked Background Color" property="colorPrimary" componentName={componentName} value={properties.colorPrimary} onPropertyChange={onPropertyChange} />
    </div>
  );
};

export const InputNumberEditor: React.FC<EditorProps> = ({ componentName, properties, onPropertyChange }) => {
  return (
    <div>
      <ColorEdit title="Background Color" property="colorBgContainer" componentName={componentName} value={properties.colorBgContainer} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Text Color" property="colorText" componentName={componentName} value={properties.colorText} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Border Color" property="borderColor" componentName={componentName} value={properties.borderColor} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Hover Border Color" property="hoverBorderColor" componentName={componentName} value={properties.hoverBorderColor} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Active Border Color" property="activeBorderColor" componentName={componentName} value={properties.activeBorderColor} onPropertyChange={onPropertyChange} />
      <ColorEdit title="Active Shadow" property="activeShadow" componentName={componentName} value={properties.activeShadow} onPropertyChange={onPropertyChange} />
    </div>
  );
};